import React, { useState } from 'react';
import Chart from 'react-apexcharts';
import { withDefaultVerticalCapacityChart } from '../../../../hoc';
import { isNumber, setSoilColorQc } from '../../../../utils';
import stringify from 'fast-json-stable-stringify';
import useDeepCompareEffect from 'use-deep-compare-effect';

const CuChart = ({
    dataToChart,
    chartOptions,
}) => {
    const [options, setOptions] = useState(chartOptions);
    const [series, setSeries] = useState([
        {
            name: '',
            data: [],
        },
    ]);

    useDeepCompareEffect(() => {
        setSeries(() => {
            return [
                {
                    name: 'Z',
                    data: [
                        ...dataToChart.sort((a,b) => a.z - b.z)
                        .filter(ele => (isNumber(ele.cu) && isNumber(ele.z)))
                        .reduce((acc, ele, idx, arr) => {
                            if (idx === (arr.length - 1)){
                                return [...acc, [ele.cu, ele.z], [0, ele.z]];
                            } else if(idx === 0) {
                                return [
                                    ...acc, 
                                    [ele.cu, 0], 
                                    [ele.cu, ele.z]
                                ];
                            } else {
                                return [...acc, [ele.cu, ele.z]];
                            }
                        }, []),
                    ],
                },
            ];
        });

        setOptions(() => {
            const maxZ = Math.max(...dataToChart.map((ele) => ele.z));
            const maxCu = Math.max(...dataToChart.map((ele) => ele.cu));
            const minCu = Math.min(...dataToChart.map((ele) => ele.cu));
            const maxZScale = maxZ%2 === 0 ? maxZ : maxZ + 1;

            return {
                ...chartOptions,
                dataLabels: {
                    ...chartOptions['dataLabels'],
                    offsetX: 8,
                    style: {
                        fontSize: '10px',
                        fontFamily: 'Helvetica, Arial, sans-serif',
                        fontWeight: 'bold',
                        colors: ['#000']
                    },
                },
                yaxis: {
                    ...chartOptions['yaxis'],
                    type: 'numeric',
                    tickAmount: Math.ceil(maxZ) > 30 
                        ? Math.ceil(maxZScale)/2
                        : Math.ceil(maxZScale),
                    max: Math.ceil(maxZScale),
                    min: 0,
                    forceNiceScale: true,
                    showForNullSeries: true,
                    stepSize: 2,
                    title: {
                        ...chartOptions['yaxis']['title'],
                        offsetX: 7
                    },      
                    labels: {
                        
                        offsetX: 0,
                        formatter: (val) => {
                            return val > maxZ ? '' : val.toFixed(0);
                        },
                        style: {
                            fontSize: '13px',
                        }
                    }
                },
                xaxis: {
                    ...chartOptions['xaxis'],
                    type: 'numeric',
                    labels: {
                        ...chartOptions['xaxis']['labels'],
                        offsetY: 0,

                        style: {
                            fontSize: '13px',
                        }
                    },
                    title: {
                        ...chartOptions['xaxis']['title'],
                        offsetY: 0
                    },
                    position: 'top',
                    max: Math.floor((maxCu + 10 - 1) / 10) * 10 + 10,
                    min: Math.floor(minCu),
                    axisBorder: {
                        show: false,
                    },
                    axisTicks: {
                        show: true,
                        color: '#888',
                        height: 5,
                    },
                    crosshairs: {
                        show: false,
                    }
                },
                stroke: {
                    ...chartOptions['stroke'],
                    width: 1,
                    colors: ['#111'],
                },
                tooltip: {
                    enabled: false
                },
                markers: {
                    size: 0
                },
                grid: {
                    show: false,
                    padding: {
                        right: 25,
                        left: 10
                    },  
                },
                fill: {
                    ...chartOptions['fill'],
                    colors: ['#333'],
                    opacity: 0.35,
                },
                annotations: {
                    yaxis: [
                        ...dataToChart.sort((a,b) => a.z-b.z).reduce((acc, ele, idx, arr) => {
                            return [
                                ...acc,
                                {
                                    y: idx === 0 ? 0 : arr[idx - 1].z,
                                    y2: idx === (arr.length - 1) ? Math.ceil(ele.z) : ele.z,
                                    borderColor: 'transparent',
                                    fillColor: setSoilColorQc({
                                        type: ele.soil,
                                        qc: ele.qc,
                                    }),
                                    opacity: 0.55
                                },
                            ];
                        }, []),
                    ],
                },
            };
        });
    }, [chartOptions, dataToChart]);

    return (
        <Chart
            options={options}
            series={series}
            height={650}
            type="area"
        />
    );
};

export default React.memo(
    withDefaultVerticalCapacityChart(CuChart),
    (prev, next) => stringify(prev) === stringify(next),
);
